import { type FC, useState } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { DRONE_TYPE_LABELS, DRONE_TYPE_COLORS } from '@/services/drone-attacks';
import type { DroneAttack } from '@/types';

// ---------------------------------------------------------------------------
// Drone Attack Intelligence Panel
// Sources: Telegram war channels, Twitter OSINT, UKMTO, CENTCOM
// ---------------------------------------------------------------------------

const STATUS_CONFIG = {
  inbound:     { label: '🔴 INBOUND',     color: 'text-red-400',    bg: 'bg-red-950/30 border-red-900/40' },
  impact:      { label: '💥 IMPACT',      color: 'text-orange-300', bg: 'bg-orange-950/30 border-orange-900/40' },
  intercepted: { label: '🛡 INTERCEPTED', color: 'text-sky-400',    bg: 'bg-sky-950/30 border-sky-900/40' },
  cooldown:    { label: '⏳ COOLDOWN',    color: 'text-slate-500',  bg: 'bg-slate-900/20 border-slate-800/30' },
};

const DroneRow: FC<{ drone: DroneAttack }> = ({ drone }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[drone.status];
  const isActive = drone.status === 'inbound' || drone.status === 'impact';

  return (
    <div
      className={clsx(
        'border rounded p-2 cursor-pointer transition-colors',
        isActive ? cfg.bg : 'border-slate-800/40 bg-slate-900/20 opacity-60',
      )}
      onClick={() => setExpanded((v) => !v)}
    >
      <div className="flex items-start gap-1.5">
        <span className="text-[11px] shrink-0">🚁</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={clsx('text-[9px] font-bold px-1 rounded border', cfg.bg, cfg.color)}>
              {cfg.label}
            </span>
            <span className={clsx('text-[9px] font-semibold', DRONE_TYPE_COLORS[drone.type])}>
              {DRONE_TYPE_LABELS[drone.type]}
            </span>
          </div>
          <div className="mt-0.5">
            <span className="text-[10px] font-medium text-slate-200 truncate block">{drone.firedBy}</span>
            <span className="text-[9px] text-slate-500">→ {drone.targetName}</span>
          </div>
          {drone.status === 'inbound' && (
            <div className="mt-0.5 flex items-center gap-1">
              <div className="flex-1 h-1 bg-slate-800 rounded overflow-hidden">
                <div
                  className="h-full bg-yellow-400 transition-all duration-150"
                  style={{ width: `${Math.round(drone.progress * 100)}%` }}
                />
              </div>
              <span className="text-[8px] text-slate-500">{Math.round(drone.progress * 100)}%</span>
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-800/40 space-y-1">
          {/* SNS Sources */}
          <div>
            <span className="text-[8px] text-slate-600 uppercase tracking-wide">SNS Sources</span>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {drone.snsSources.map((src, i) => (
                <span key={i} className="text-[8px] px-1 rounded bg-slate-800 text-slate-400">{src}</span>
              ))}
            </div>
          </div>
          {/* Related news */}
          {drone.relatedNews.map((n, i) => (
            <div key={i} className="text-[9px] text-slate-400 leading-relaxed border-l-2 border-yellow-900/50 pl-1.5">
              <span className="text-slate-300">{n.title}</span>
              <span className="block text-[8px] text-slate-600 mt-0.5">{n.source} · {n.publishedAt.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DronePanel: FC = () => {
  const droneAttacks = useAppStore((s) => s.droneAttacks);
  const [filter, setFilter] = useState<'all' | 'inbound' | 'shahed' | 'fpv'>('all');

  const filtered = droneAttacks.filter((d) => {
    if (filter === 'inbound') return d.status === 'inbound' || d.status === 'impact';
    if (filter === 'shahed') return d.type === 'shahed';
    if (filter === 'fpv') return d.type === 'fpv' || d.type === 'loitering' || d.type === 'switchblade';
    return true;
  });

  const activeCount = droneAttacks.filter((d) => d.status === 'inbound' || d.status === 'impact').length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-yellow-400">🚁 Drone Attacks</span>
          {activeCount > 0 && (
            <span className="text-[8px] px-1.5 py-0.5 rounded bg-yellow-950/60 text-yellow-300 border border-yellow-900/40 animate-pulse">
              {activeCount} ACTIVE
            </span>
          )}
        </div>
        <span className="text-[9px] text-slate-600 italic">SNS+OSINT</span>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-1 px-3 shrink-0">
        {(['all', 'inbound', 'shahed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f === filter ? 'all' : f)}
            className={clsx(
              'rounded p-1.5 text-center border transition-colors',
              filter === f
                ? 'bg-slate-700 border-slate-600'
                : 'bg-slate-900/40 border-slate-800/50 hover:bg-slate-800/40',
            )}
          >
            <div className="text-[11px] font-bold text-yellow-400">
              {f === 'all' ? droneAttacks.length
                : f === 'inbound' ? activeCount
                : droneAttacks.filter((d) => d.type === 'shahed').length}
            </div>
            <div className="text-[8px] text-slate-600">
              {f === 'all' ? 'Total' : f === 'inbound' ? 'Active' : 'Shahed'}
            </div>
          </button>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="px-3 flex gap-1 shrink-0">
        {(['all', 'inbound', 'shahed', 'fpv'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              'px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors border',
              filter === f
                ? 'bg-yellow-900/50 text-yellow-300 border-yellow-800'
                : 'text-slate-500 border-slate-800 hover:text-slate-300',
            )}
          >
            {f === 'all' ? 'All' : f === 'inbound' ? '🔴 Active' : f === 'shahed' ? 'Shahed' : 'FPV/Loitering'}
          </button>
        ))}
      </div>

      {/* Drone list */}
      <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-1 scrollbar-thin">
        {filtered.map((d) => (
          <DroneRow key={d.id} drone={d} />
        ))}
        {filtered.length === 0 && (
          <div className="text-[10px] text-slate-600 text-center py-4">No active incidents</div>
        )}
      </div>
    </div>
  );
};
